import { useId, useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { DragGrip, XClose } from '@/assets/icons'
import type { TagCellDraft } from '../hooks/useTableCellDrafts'
import type { ColumnTag } from '../schemas'
import { tagContent, tagItems } from '../editor/tag-content'
import TableReferenceLink from './TableReferenceLink'
import TableTag from './TableTag'

/** Tags and footnote occurrences can each move to any position in the cell. */
export default function TagCellSelections({
  options,
  draft,
  matterId,
  saving,
  onDraftChange,
  onRemoveTag,
  children,
}: {
  options: ColumnTag[]
  draft: TagCellDraft
  matterId?: string
  saving: boolean
  onDraftChange: (draft: TagCellDraft) => void
  onRemoveTag: (label: string) => void
  children: ReactNode
}) {
  const id = useId()
  const dragged = useRef<number | null>(null)
  const pendingFocus = useRef<number | null>(null)
  const handles = useRef(new Map<number, HTMLButtonElement>())
  const tokens = useRef(new Map<number, HTMLSpanElement>())
  const [dropTarget, setDropTarget] = useState<{ index: number; after: boolean } | null>(null)
  const items = tagItems(draft.content)
  const numbers = new Map(draft.references.map((reference, index) => [reference.id, index + 1]))

  useLayoutEffect(() => {
    if (pendingFocus.current === null) return
    handles.current.get(pendingFocus.current)?.focus({ preventScroll: true })
    pendingFocus.current = null
  }, [draft])

  const move = (from: number, boundary: number) => {
    if (saving || !items[from]) return
    const to = boundary > from ? boundary - 1 : boundary
    if (to === from) return
    const next = [...items]
    const moved = next.splice(from, 1)
    next.splice(to, 0, ...moved)
    pendingFocus.current = to
    onDraftChange({ ...draft, content: tagContent(next) })
  }
  const finishDrag = () => {
    dragged.current = null
    setDropTarget(null)
  }
  const nearestTarget = (event: DragEvent<HTMLDivElement>) => {
    let nearest = null
    let distance = Infinity
    for (const [index, token] of tokens.current) {
      const rect = token.getBoundingClientRect()
      const dx = Math.max(rect.left - event.clientX, 0, event.clientX - rect.right)
      const dy = Math.max(rect.top - event.clientY, 0, event.clientY - rect.bottom)
      const nextDistance = dx * dx + dy * dy
      const contains = event.target instanceof Node && token.contains(event.target)
      if (contains || nextDistance < distance) {
        nearest = { index, after: event.clientX >= rect.left + rect.width / 2 }
        distance = nextDistance
        if (contains) break
      }
    }
    return nearest
  }
  const showDropTarget = (event: DragEvent<HTMLDivElement>) => {
    if (saving || dragged.current === null) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDropTarget(nearestTarget(event))
  }

  return (
    <div
      role="group"
      aria-label="Selected tags and footnotes"
      className="flex min-h-8 min-w-0 flex-wrap items-center gap-1.5"
      onDragEnter={showDropTarget}
      onDragOver={showDropTarget}
      onDragLeave={(event) => {
        if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDropTarget(null)
      }}
      onDrop={(event) => {
        if (saving || dragged.current === null) return
        event.preventDefault()
        event.stopPropagation()
        const target = nearestTarget(event)
        if (target) move(dragged.current, target.index + Number(target.after))
        finishDrag()
      }}
    >
      <span id={`${id}-move-help`} className="sr-only">
        Drag tags or footnotes to move them independently. Use the left or right arrow key on a move handle to move it one position.
      </span>
      {items.map((item, index) => {
        const reference = draft.references.find((reference) => reference.id === item.value)
        const number = numbers.get(item.value)
        const location =
          items
            .slice(0, index)
            .filter((item) => item.kind === 'tag')
            .at(-1)?.value ?? 'before tags'
        const tagLabel = item.kind === 'tag' && <span className="min-w-0 break-words text-left">{item.value}</span>
        const moveHandle = (
          <button
            ref={(element) => {
              if (element) handles.current.set(index, element)
              else handles.current.delete(index)
            }}
            type="button"
            draggable={!saving}
            aria-label={item.kind === 'tag' ? `Move tag ${item.value}` : `Move footnote ${number ?? '?'} from ${location}`}
            aria-describedby={`${id}-move-help`}
            title="Drag to move, or use the left and right arrow keys"
            disabled={saving}
            className={clsx(
              'flex cursor-grab items-center justify-center rounded-sm hover:bg-black/10 active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500',
              item.kind === 'tag' ? 'min-h-4 min-w-0' : 'min-h-5 w-3.5 shrink-0'
            )}
            onDragStart={(event) => {
              if (saving) {
                event.preventDefault()
                return
              }
              dragged.current = index
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('application/x-paxton-table-cell-item', item.value)
            }}
            onDragEnd={finishDrag}
            onKeyDown={(event) => {
              if (saving || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
              event.preventDefault()
              if (event.key === 'ArrowLeft' && index > 0) move(index, index - 1)
              if (event.key === 'ArrowRight' && index < items.length - 1) move(index, index + 2)
            }}
          >
            <DragGrip aria-hidden className="size-3 shrink-0" />
            {tagLabel}
          </button>
        )
        return (
          <span
            key={index}
            ref={(element) => {
              if (element) tokens.current.set(index, element)
              else tokens.current.delete(index)
            }}
            role="group"
            aria-label={item.kind === 'tag' ? `Tag ${item.value}` : `Footnote ${number ?? '?'} for ${location}`}
            className="relative inline-flex max-w-full items-center rounded"
          >
            {dropTarget?.index === index && (
              <span
                aria-hidden
                className={clsx('pointer-events-none absolute -inset-y-1 w-0.5 rounded bg-brand-500', dropTarget.after ? '-right-1.5' : '-left-1.5')}
              />
            )}
            {item.kind === 'tag' ? (
              <TableTag tag={options.find((tag) => tag.label === item.value) ?? { label: item.value, color: 'Neutral', custom_color: null }}>
                {items.length > 1 ? moveHandle : tagLabel}
                <button
                  type="button"
                  aria-label={`Remove ${item.value}`}
                  disabled={saving}
                  className="ml-1 flex size-4 shrink-0 items-center justify-center rounded-sm hover:bg-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                  onClick={() => onRemoveTag(item.value)}
                >
                  <XClose aria-hidden className="size-3" />
                </button>
              </TableTag>
            ) : (
              <span className="inline-flex shrink-0 items-center rounded border border-brand-neutral-300 bg-brand-neutral-200 text-xs text-brand-neutral-800">
                {moveHandle}
                {reference && matterId ? (
                  <TableReferenceLink
                    reference={reference}
                    matterId={matterId}
                    className="inline-flex min-h-5 min-w-4 items-center justify-center px-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                  >
                    {number}
                  </TableReferenceLink>
                ) : (
                  <span className="px-0.5" title={reference?.document_name ?? 'Source unavailable'}>
                    {number ?? '?'}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Remove footnote ${number ?? '?'} from ${location}`}
                  disabled={saving}
                  className="flex size-5 items-center justify-center rounded-sm hover:bg-brand-neutral-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                  onClick={() => {
                    if (!saving) onDraftChange({ ...draft, content: tagContent(items.filter((_, itemIndex) => itemIndex !== index)) })
                  }}
                >
                  <XClose aria-hidden className="size-3" />
                </button>
              </span>
            )}
          </span>
        )
      })}
      {children}
    </div>
  )
}
